import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Grid2, Alert, CircularProgress, Fab, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import { FreightCard } from './FreightCard';
import { FilterBar } from './FilterBar';
// import { DateRangeDialog } from './DateRangeDialog';
import type { FreightDataChange, TimeFilterTabs } from '@/utilities/etransportTypes';
import { getStartOfDayAsMicros, getEndOfDayAsMicros } from '@/utilities/timeUtils';
import { ETransportFreightPlutoDto, MinimalTarget } from '@/generated/model';
import { useTranslation } from 'react-i18next';
import './etransport.css';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

export function FreightCardGridView({
  targets,
  freights,
  isLoading,
  searchTarget,
  currentFreight,
  onRefetch,
  setNewChanges,
  setSearchTarget,
  setCurrentFreight,
  setOpenFreightSubmitForm,
  setOpenTargetFreightDetails,
  setOpenTargetFreightSubmitForm,
}: {
  targets: MinimalTarget[] | undefined;
  freights: ETransportFreightPlutoDto[] | undefined;
  isLoading: boolean;
  searchTarget: string | undefined;
  currentFreight: ETransportFreightPlutoDto | undefined;
  onRefetch: () => void;
  setNewChanges: Dispatch<SetStateAction<FreightDataChange | null>>;
  setSearchTarget: Dispatch<SetStateAction<string | undefined>>;
  setCurrentFreight: Dispatch<SetStateAction<ETransportFreightPlutoDto | undefined>>;
  setOpenFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
  setOpenTargetFreightDetails: Dispatch<SetStateAction<boolean>>;
  setOpenTargetFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const [startTime, setStartTime] = useState<number>(() => Date.now());
  const [activeTab, setActiveTab] = useState<TimeFilterTabs>('Active');

  /* AFTER MVP */
  // const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState<boolean>(false);
  // const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({
  //   start: '',
  //   end: '',
  // });
  /* AFTER MVP */

  useEffect(() => {
    const interval = setInterval(() => setStartTime(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  function handleOpenFreightSubmitForm(): void {
    setOpenFreightSubmitForm(true);
    setOpenTargetFreightDetails(false);
    setOpenTargetFreightSubmitForm(false);
    setCurrentFreight(undefined);
  }

  function getTimeFilteredFreights(
    freights: ETransportFreightPlutoDto[] | undefined,
    activeTab: TimeFilterTabs,
    startTime: number
  ): ETransportFreightPlutoDto[] | undefined {
    if (activeTab === 'All') return freights;
    const todayStart = getStartOfDayAsMicros(startTime * 1000);
    const todayEnd = getEndOfDayAsMicros(startTime * 1000);

    return freights?.filter((freight) => {
      const validityEndDay = freight.validityEndTimeMicros || 0;
      const validityStartDay = freight.validityStartTimeMicros || 0;

      switch (activeTab) {
        case 'Future':
          return validityStartDay > todayEnd;
        case 'Inactive':
          return validityEndDay < todayStart;
        case 'Active':
          return validityEndDay >= todayStart && validityStartDay <= todayEnd;
        default:
          return true;
      }
    });
  }

  const filteredFreights: ETransportFreightPlutoDto[] | undefined = useMemo(() => {
    if (freights === undefined) return undefined;
    const filteredFreights = getTimeFilteredFreights(freights?.slice(), activeTab, startTime);
    return filteredFreights?.sort((a, b) => {
      if (a.validityStartTimeMicros === undefined || b.validityStartTimeMicros === undefined) return 0;
      return a.validityStartTimeMicros - b.validityStartTimeMicros;
    });
  }, [freights, activeTab, startTime]);

  const categories: Map<TimeFilterTabs, number> = useMemo(() => {
    const todayStart = getStartOfDayAsMicros(startTime * 1000);
    const todayEnd = getEndOfDayAsMicros(startTime * 1000);

    const counts = new Map<TimeFilterTabs, number>([
      ['All', freights?.length || 0],
      ['Inactive', 0],
      ['Active', 0],
      ['Future', 0],
    ]);

    freights?.forEach((freight) => {
      const validityEndDay = getEndOfDayAsMicros(freight.validityEndTimeMicros);
      const validityStartDay = getStartOfDayAsMicros(freight.validityStartTimeMicros);

      if (validityEndDay < todayStart) {
        counts.set('Inactive', (counts.get('Inactive') ?? 0) + 1);
      } else if (validityStartDay > todayEnd) {
        counts.set('Future', (counts.get('Future') ?? 0) + 1);
      } else if (validityEndDay >= todayStart && validityStartDay <= todayEnd) {
        counts.set('Active', (counts.get('Active') ?? 0) + 1);
      }
    });

    return counts;
  }, [freights, startTime]);

  /* AFTER MVP */
  // const handleDateRangeApply = () => {
  //   if (customDateRange.start && customDateRange.end) {
  //     setTimeFilter('custom');
  //     setDateRangeDialogOpen(false);
  //   }
  // };

  // const handleClearDateRange = () => {
  //   setTimeFilter('all');
  //   setCustomDateRange({ start: '', end: '' });
  // };
  /* AFTER MVP */

  const freightsDidNotLoad: boolean = !isLoading && !freights && !targets;
  const freightsEmpty: boolean = !isLoading && !freightsDidNotLoad && !filteredFreights?.length;
  const freightsOk: boolean = !isLoading && !freightsEmpty;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: isPhoneOrHighScreen() ? '100%' : '100vh',
      }}
    >
      <div className={`topBar ${isPhoneOrHighScreen() ? 'phone' : ''}`}>
        <FilterBar
          isLoading={isLoading}
          activeTab={activeTab}
          categories={categories}
          searchTarget={searchTarget}
          setActiveTab={setActiveTab}
          onSearchChange={setSearchTarget}
        />
      </div>

      <div
        className="panelOverlay"
        style={{
          paddingBottom: isPhoneOrHighScreen() ? 56 : 75,
          paddingLeft: isPhoneOrHighScreen() ? '1%' : '0',
        }}
      >
        {isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            flexDirection="column"
            gap={2}
          >
            <CircularProgress /> {t('etransport.cardview.loading')}
          </Box>
        )}

        {freightsDidNotLoad && (
          <>
            <Alert severity="error">{t('etransport.freightfrom.apierror')}</Alert>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
              flexDirection="column"
              gap={2}
            >
              <img
                src="svg/NoSearchResult_ez_transparent.svg"
                alt="not_found"
                style={{ width: '100%', maxHeight: '250px' }}
                data-qa-element-id="no_result_image"
              />

              <Typography variant="h6" color="text.secondary" textAlign="center">
                {t('etransport.cardview.noload')}
              </Typography>
              <Button
                variant="outlined"
                onClick={onRefetch}
                sx={{
                  borderRadius: '25px',
                  boxShadow: 4,
                  backgroundColor: 'white',
                  color: 'var(--mui-palette-primary-main)',
                  border: '2px solid var(--mui-palette-primary-main)',
                  '&:hover': {
                    backgroundColor: 'rgba(21, 61, 146, 0.1)',
                  },
                }}
              >
                <ReplayIcon sx={{ mr: 1 }} />
                {t('etransport.cardview.retry')}
              </Button>
            </Box>
          </>
        )}

        {freightsEmpty && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            flexDirection="column"
            gap={2}
          >
            <img
              src="svg/NoSearchResult_ez_transparent.svg"
              alt="not_found"
              style={{ width: '100%', maxHeight: '250px' }}
              data-qa-element-id="no_result_image"
            />

            <Typography variant="h6" color="text.secondary" textAlign="center">
              {t('etransport.cardview.nofreights')}
            </Typography>
            {(searchTarget || activeTab !== 'Active') && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTarget('');
                  setActiveTab('All');
                }}
                sx={{
                  borderRadius: '25px',
                  boxShadow: 4,
                  backgroundColor: 'white',
                  color: 'var(--mui-palette-primary-main)',
                  border: '2px solid var(--mui-palette-primary-main)',
                  '&:hover': {
                    backgroundColor: 'rgba(21, 61, 146, 0.1)',
                  },
                }}
              >
                <CloseIcon sx={{ mr: 1 }} /> {t('etransport.cardview.deletesearch')}
              </Button>
            )}
          </Box>
        )}

        {freightsOk && (
          <>
            <Grid2 container spacing={1}>
              {filteredFreights
                ?.slice()
                .sort((a, b) => (b.validityStartTimeMicros || 0) - (a.validityStartTimeMicros || 0))
                .map((freight, index) => (
                  <Grid2 container key={index}>
                    <FreightCard
                      active={freight.uit === currentFreight?.uit}
                      freight={freight}
                      targets={targets}
                      startTime={startTime}
                      setCurrentFreight={setCurrentFreight}
                      setOpenTargetFreightDetails={setOpenTargetFreightDetails}
                      setOpenTargetFreightSubmitForm={setOpenTargetFreightSubmitForm}
                      setOpenFreightSubmitForm={setOpenFreightSubmitForm}
                      setNewChanges={setNewChanges}
                    />
                  </Grid2>
                ))}
            </Grid2>

            {/* AFTER MVP */}
            {/* <DateRangeDialog
              open={dateRangeDialogOpen}
              customDateRange={customDateRange}
              onClose={() => setDateRangeDialogOpen(false)}
              onDateRangeChange={setCustomDateRange}
              onApply={handleDateRangeApply}
            /> */}
            {/* AFTER MVP */}
          </>
        )}
      </div>

      <Stack
        spacing={1}
        sx={{
          position: 'absolute',
          bottom: isPhoneOrHighScreen() ? 5 : 20,
          right: isPhoneOrHighScreen() ? 10 : 18,
        }}
      >
        <>
          <Fab
            variant="extended"
            onClick={handleOpenFreightSubmitForm}
            aria-label={t('etransport.cardview.add')}
            sx={{
              boxShadow: 6,
              backgroundColor: 'var(--mui-palette-secondary-main)',
              color: 'white',
              border: '2px solid white',
              '&:hover': {
                backgroundColor: 'var(--mui-palette-secondary-light)',
              },
            }}
          >
            <AddIcon sx={{ mr: 1 }} />
            {t('etransport.cardview.add')}
          </Fab>
        </>
      </Stack>
    </Box>
  );
}
